import React from 'react';
import { Button } from '@/components/ui/button';
import { useQuickscan } from '@/contexts/QuickscanContext';
import { ArrowRight, Check, Sun, Thermometer, Lightbulb, Battery } from 'lucide-react';

const solutions = [
  { id: 'solar', label: 'Zonnepanelen', icon: Sun, description: 'Duurzame energieopwekking' },
  { id: 'heat-pump', label: 'Warmtepomp', icon: Thermometer, description: 'Efficiënte verwarming/koeling' },
  { id: 'led', label: 'LED Verlichting', icon: Lightbulb, description: 'Energiezuinige verlichting' },
  { id: 'battery', label: 'Batterijopslag', icon: Battery, description: 'Energie opslaan voor later gebruik' },
];

export const CurrentSolutionsScreen = () => {
  const { data, updateData, nextStep, prevStep } = useQuickscan();

  const toggleSolution = (solutionId: string) => {
    const currentSolutions = [...data.currentSolutions];
    const index = currentSolutions.indexOf(solutionId);
    
    if (index > -1) {
      currentSolutions.splice(index, 1);
    } else {
      currentSolutions.push(solutionId);
    }
    
    updateData('currentSolutions', currentSolutions);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-energy-green mb-2">
          Welke energieoplossingen heb je al?
        </h2>
        <p className="text-gray-600">
          Dit helpt ons om aanvullende aanbevelingen te doen
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {solutions.map((solution) => {
          const Icon = solution.icon;
          const isSelected = data.currentSolutions.includes(solution.id);
          
          return (
            <div
              key={solution.id}
              onClick={() => toggleSolution(solution.id)}
              className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                isSelected 
                  ? 'border-energy-green bg-energy-green/5 shadow-md animate-glow' 
                  : 'border-gray-200 hover:border-energy-green/50 hover:shadow-sm'
              }`}
            >
              {isSelected && (
                <div className="absolute top-3 right-3">
                  <div className="w-6 h-6 bg-energy-green rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
              
              <Icon className={`w-10 h-10 mb-4 ${
                isSelected ? 'text-energy-green' : 'text-gray-600'
              }`} />
              
              <h3 className="text-lg font-semibold mb-2">{solution.label}</h3>
              <p className="text-sm text-gray-600">{solution.description}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
        <div className="text-center">
          <h3 className="font-semibold mb-2">
            {data.currentSolutions.length === 0 
              ? 'Nog geen energieoplossingen? Geen probleem!' 
              : `${data.currentSolutions.length} oplossing(en) geselecteerd`
            }
          </h3>
          <p className="text-sm text-gray-600">
            {data.currentSolutions.length === 0 
              ? 'We laten je zien welke opties het meest geschikt zijn voor jouw situatie.'
              : 'Prima! We kijken welke aanvullende oplossingen je nog meer voordeel kunnen bieden.'
            }
          </p>
        </div>
      </div>

      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={prevStep}
          className="border-gray-300"
        >
          Vorige
        </Button>
        
        <Button 
          onClick={nextStep}
          className="bg-[#014336] text-white hover:bg-[#014336]/90"
        >
          Volgende <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};
